import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { SubTopic } from '@shapeable/gesda-types';
import { ContentTitle, useInViewEffect } from '@shapeable/ui';
import { sortBy } from 'lodash';
import { SubTopicViewLayout } from './sub-topic-view-layout';
import { subTopicAnchorId } from '@shapeable/gesda-ui';
const cls = classNames('sub-topics-list-layout');

export type SubTopicsListLayoutProps = Classable & {
  items?: SubTopic[];
};

type AnimationProps = {
  $isAnimationActive?: boolean;
  color?: string;
};

const ContainerStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(8)};
  `,
});

const ItemStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(10)};

    /* Pull hash targets higher so SubTopic titles align correctly in the Topic layout. */
    scroll-margin-top: 90px;
  `,
});

const TitleStyles = breakpoints({
  base: css`
    position: relative;
    margin-bottom: 0;
    font-size: 1.8em;
    padding-bottom: ${theme.UNIT(2)};
    width: max-content;
    max-width: 100%;
    line-height: 1;

    opacity: 0.55;
    transform: translateY(${theme.UNIT(2)});
    transition: opacity 0.5s ease, transform 0.5s ease;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 2px;
      background: ${({ color }: AnimationProps) => color || theme.COLOR('primary')};
      transform: scaleX(0);
      transform-origin: left center;
      transition: transform 0.5s ease 0.12s;
    }

    ${({ $isAnimationActive }: AnimationProps) => $isAnimationActive && css`
      opacity: 1;
      transform: translateY(0);

      &::after {
        transform: scaleX(1);
      }
    `}

    @media (prefers-reduced-motion: reduce) {
      opacity: 1;
      transform: none;
      transition: none;

      &::after {
        transform: scaleX(1);
        transition: none;
      }
    }
  `,
});

const SubTopicViewStyles = breakpoints({
  base: css`
    padding-top: 0;
  `,
});

const My = {
  Container: styled.div`${ContainerStyles}`,
    Item: styled.section`${ItemStyles}`,
      Title: styled(ContentTitle)<AnimationProps>`${TitleStyles}`,
      SubTopicView: styled(SubTopicViewLayout)`${SubTopicViewStyles}`,
};

type SubTopicListItemProps = {
  entity: SubTopic;
};

const SubTopicListItem: React.FC<SubTopicListItemProps> = ({ entity }) => {
  const [isAnimationActive, setIsAnimationActive] = React.useState(false);

  const { color } = entity || {};
  // Per-item observer: shared refs only attach to the last mapped node.
  // Low threshold so a tall section still fires when the title enters view (hash jump + scroll-margin).
  const ref = useInViewEffect(
    () => {
      setIsAnimationActive(true);
    },
    {
      threshold: 0.10,
      triggerOnce: true,
    },
  );

  return (
    <My.Item ref={ref} id={subTopicAnchorId(entity)}>
      <My.Title color={color?.value} $isAnimationActive={isAnimationActive}>{entity.name}</My.Title>
      <My.SubTopicView entity={entity} />
    </My.Item>
  );
};

export const SubTopicsListLayout: Shapeable.FC<SubTopicsListLayoutProps> = (props) => {
  const { className, items = [] } = props;

  if (!items.length) return null;

  return (
    <My.Container className={cls.name(className)}>
      {
        sortBy(items, 'outlineNumber').map((subTopic) => (
          <SubTopicListItem
            key={subTopic.id || subTopic.slug}
            entity={subTopic}
          />
        ))
      }
    </My.Container>
  );
};

SubTopicsListLayout.cls = cls;
